/**
 * @file BinLabelPrintFields.tsx
 * @description Поля диалога печати этикеток ячеек (phase19.22 шаг 5): что
 *   печатаем (ячейки / + полки / + стеллаж), копий каждой этикетки 1–5 (так
 *   считает сервер: копии и у полок, и у стеллажа) и шаблон.
 *
 * Сегменты-кнопки 56px с `aria-pressed` и галочки в строках 56px — тач-цели
 * склада (правила warehouse-UI), как в «Коды на этикетке».
 */

import { BIN_LABEL_MAX_COPIES, levelCounts } from "./bin-label-print";
import type { BinLabelPrintForm } from "./useBinLabelPrintForm";
import { SectionLabel } from "../../ui/SectionLabel";
import { LABEL_TEMPLATE_META, type BinLabelTemplate } from "../../../lib/api-labels";
import type { StorageBinDto } from "../../../lib/api-bins";
import { pluralRu } from "../../../lib/plural";
import { cn } from "@/lib/utils";

/** Шаблоны ячеек: «Компакт» первым — он по умолчанию. */
const TEMPLATES: BinLabelTemplate[] = ["COMPACT", "STANDARD"];
const COPIES = Array.from({ length: BIN_LABEL_MAX_COPIES }, (_, i) => i + 1);

interface Props {
  form: BinLabelPrintForm;
  selected: StorageBinDto[];
  /** Стеллаж целиком — подпись «стеллаж A целиком». */
  rack: string | null;
}

function segmentClass(active: boolean): string {
  return cn(
    "min-h-14 rounded-xl px-2 text-sm transition-colors",
    active ? "bg-primary font-medium text-on-primary" : "bg-surface-low text-on-surface-variant hover:bg-accent",
  );
}

/** Строка-галочка 56px. */
function CheckRow({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="flex min-h-14 cursor-pointer items-center gap-3 rounded-xl bg-surface-low px-3 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-5 accent-primary"
      />
      <span>{label}</span>
    </label>
  );
}

export function BinLabelPrintFields({ form, selected, rack }: Props) {
  const { shelves, racks } = levelCounts(selected);
  const cells = `${selected.length} ${pluralRu(selected.length, ["ячейка", "ячейки", "ячеек"])}`;

  return (
    <>
      <div className="flex flex-col gap-2">
        <SectionLabel as="div">Что печатаем</SectionLabel>
        <p className="text-sm">
          Ячейки: {cells}
          {rack ? ` — стеллаж ${rack} целиком` : ""}
        </p>
        <CheckRow
          checked={form.includeShelfLabels}
          onChange={form.setIncludeShelfLabels}
          label={`+ этикетки полок (${shelves})`}
        />
        <CheckRow
          checked={form.includeRackLabel}
          onChange={form.setIncludeRackLabel}
          label={racks === 1 ? "+ этикетка стеллажа" : `+ этикетки стеллажей (${racks})`}
        />
      </div>

      <div className="flex flex-col gap-2">
        <SectionLabel as="div">Копий каждой этикетки</SectionLabel>
        <div role="group" aria-label="Копий каждой этикетки" className="grid grid-cols-5 gap-1.5">
          {COPIES.map((n) => (
            <button
              key={n}
              type="button"
              aria-pressed={form.copies === n}
              onClick={() => form.setCopies(n)}
              className={cn(segmentClass(form.copies === n), "tabular-nums")}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <SectionLabel as="div">Шаблон ячеек</SectionLabel>
        <div role="group" aria-label="Шаблон этикеток ячеек" className="grid grid-cols-2 gap-1.5">
          {TEMPLATES.map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={form.template === t}
              onClick={() => form.setTemplate(t)}
              className={segmentClass(form.template === t)}
            >
              {LABEL_TEMPLATE_META[t].title}
            </button>
          ))}
        </div>
        {form.includeRackLabel ? (
          <p className="text-xs text-on-surface-variant">
            {/* Разные шаблоны — разная самоклейка: «те же листы» ввели бы в заблуждение. */}
            {form.template === "STANDARD"
              ? "Этикетка стеллажа — тоже «Стандарт», на тех же листах A4."
              : `Этикетка стеллажа — «${LABEL_TEMPLATE_META.STANDARD.title}»: в том же PDF, но на своём листе самоклейки A4.`}
          </p>
        ) : null}
      </div>
    </>
  );
}
